import React, { useState } from 'react';
import { CompleteKundali, Language, SadeSatiDhaiyyaPeriod } from '../types/kundali';
import { Sparkles, Calendar, Clock, AlertTriangle, CheckCircle2, ShieldAlert, ArrowRight, Filter, ChevronDown, ChevronUp, Moon, Compass, Info } from 'lucide-react';
import { AlertBadge } from './AlertBadge';

interface ShaniSadeSatiTimelineProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const ShaniSadeSatiTimeline: React.FC<ShaniSadeSatiTimelineProps> = ({ kundali, lang = 'en' }) => {
  const { sadeSati, birthDetails } = kundali;
  const timeline = sadeSati?.timeline || [];

  const [activeTab, setActiveTab] = useState<'ACTIVE' | 'UPCOMING' | 'LIFETIME'>('ACTIVE');
  const [activeFilter, setActiveFilter] = useState<'ALL' | 'SADE_SATI' | 'DHAIYYA'>('ALL');
  const [expandedPeriodId, setExpandedPeriodId] = useState<string | null>(
    sadeSati?.activePeriodDetails?.id || (timeline.find(p => p.status === 'Active' || p.status === 'Upcoming')?.id || null)
  );

  const activePeriod = timeline.find((p) => p.status === 'Active');
  const upcomingPeriods = timeline.filter((p) => p.status === 'Upcoming');
  const pastPeriods = timeline.filter((p) => p.status === 'Past');

  const filteredTimeline = timeline.filter((period) => {
    if (activeTab === 'ACTIVE') {
      if (period.status !== 'Active') return false;
    } else if (activeTab === 'UPCOMING') {
      if (period.status !== 'Upcoming') return false;
    }
    // If activeTab === 'LIFETIME', show all or filtered by category
    if (activeFilter === 'SADE_SATI') return period.category === 'Sade Sati';
    if (activeFilter === 'DHAIYYA') return period.category === 'Dhaiyya';
    return true;
  });

  const [isSatsOpen, setIsSatsOpen] = useState<boolean>(Boolean(activePeriod));

  const toggleExpand = (id: string) => {
    setExpandedPeriodId(prev => prev === id ? null : id);
  };

  if (!activePeriod && !isSatsOpen && activeTab === 'ACTIVE') {
    return (
      <div 
        onClick={() => setIsSatsOpen(true)}
        className="bg-astro-bg border border-astro-border rounded-2xl p-4 shadow-xs flex items-center justify-between cursor-pointer hover:border-astro-primary/40 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="bg-emerald-50 p-2.5 rounded-xl text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-bold text-astro-primary font-serif text-sm">
              {lang === 'hi' ? 'साढ़े साती एवं ढैय्या' : 'Sade Sati and Dhaiyya'}
            </h4>
            <p className="text-xs text-emerald-700 font-medium">
              {lang === 'hi' ? 'वर्तमान में कोई सक्रिय साढ़े साती / ढैय्या नहीं • विवरण हेतु क्लिक करें' : 'No Active Sade Sati / Dhaiyya Currently • Click to view full timeline'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-astro-secondary-text font-bold">
          <span>{lang === 'hi' ? 'देखें' : 'View'}</span>
          <ChevronDown className="w-4 h-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-astro-bg border-2 border-astro-primary/30 rounded-2xl p-5 sm:p-6 shadow-md space-y-5 relative">
      {!activePeriod && (
        <button 
          onClick={() => setIsSatsOpen(false)}
          className="absolute top-4 right-4 text-xs font-bold text-astro-secondary-text hover:text-astro-primary bg-astro-card px-2.5 py-1 rounded-lg border border-astro-border"
        >
          {lang === 'hi' ? 'सकुंचित करें' : 'Collapse'}
        </button>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-4">
        <div className="flex items-center gap-3">
          <div className="bg-astro-primary p-2.5 rounded-xl text-white shadow-xs">
            <Moon className="w-6 h-6 text-astro-accent" />
          </div>
          <div>
            <h3 className="font-bold text-astro-primary font-serif text-lg sm:text-xl flex items-center gap-2">
              {lang === 'hi' ? 'साढ़े साती एवं ढैय्या' : 'Sade Sati and Dhaiyya'}
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-astro-primary/10 text-astro-primary font-sans font-semibold">
                Classical Parashari
              </span>
            </h3>
            <p className="text-xs text-astro-secondary-text">
              {lang === 'hi'
                ? `जन्म चंद्र: ${sadeSati.moonSign} | वर्तमान गोचर शनि: ${sadeSati.saturnSign} | शास्त्रीय काल निर्धारण`
                : `Natal Moon: ${sadeSati.moonSign} | Current Transit Saturn: ${sadeSati.saturnSign} | Saturn Transit Horizons`}
            </p>
          </div>
        </div>

        {/* Current Active Status Pill */}
        {activePeriod ? (
          <div className="flex items-center gap-2 bg-astro-secondary-surface border border-[#E2DED5] px-3 py-1.5 rounded-xl text-xs font-bold text-astro-secondary shadow-xs">
            <span className="w-2.5 h-2.5 rounded-full bg-astro-secondary-surface0 animate-pulse" />
            <span>
              {activePeriod.category === 'Sade Sati' ? 'Sade Sati Active' : 'Dhaiyya Active'}: {activePeriod.formattedStartDate} → {activePeriod.formattedEndDate}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-300 px-3 py-1.5 rounded-xl text-xs font-bold text-emerald-800 shadow-xs">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span>{lang === 'hi' ? 'वर्तमान में कोई सक्रिय साढ़े साती / ढैय्या नहीं' : 'No Active Sade Sati / Dhaiyya Currently'}</span>
          </div>
        )}
      </div>

      {/* Primary Navigation Tabs: Active Transit (Default) | Upcoming Transits | Lifetime History */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-astro-border/60 pb-3">
        <div className="flex items-center gap-2 bg-astro-card p-1 rounded-xl border border-astro-border">
          <button
            onClick={() => setActiveTab('ACTIVE')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeTab === 'ACTIVE'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-bg'
            }`}
          >
            ⚡ {lang === 'hi' ? 'सक्रिय गोचर (Default)' : 'Active Transit (Default)'}
          </button>
          <button
            onClick={() => setActiveTab('UPCOMING')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeTab === 'UPCOMING'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-bg'
            }`}
          >
            ⏳ {lang === 'hi' ? 'आगामी गोचर' : 'Upcoming Transits'} ({upcomingPeriods.length})
          </button>
          <button
            onClick={() => setActiveTab('LIFETIME')}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeTab === 'LIFETIME'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-bg'
            }`}
          >
            📁 {lang === 'hi' ? 'जीवनकाल इतिहास' : 'Lifetime History'} ({timeline.length})
          </button>
        </div>

        {activeTab === 'LIFETIME' && (
          <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-astro-border text-xs">
            <button
              onClick={() => setActiveFilter('ALL')}
              className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${activeFilter === 'ALL' ? 'bg-astro-primary/10 text-astro-primary' : 'text-astro-secondary-text'}`}
            >
              {lang === 'hi' ? 'सभी' : 'All'}
            </button>
            <button
              onClick={() => setActiveFilter('SADE_SATI')}
              className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${activeFilter === 'SADE_SATI' ? 'bg-astro-primary/10 text-astro-primary' : 'text-astro-secondary-text'}`}
            >
              Sade Sati
            </button>
            <button
              onClick={() => setActiveFilter('DHAIYYA')}
              className={`px-2.5 py-1 rounded-md text-[11px] font-bold ${activeFilter === 'DHAIYYA' ? 'bg-astro-primary/10 text-astro-primary' : 'text-astro-secondary-text'}`}
            >
              Dhaiyya
            </button>
          </div>
        )}
      </div>

      {/* Active Period Spotlight Banner (if active) */}
      {activePeriod && (
        <div className="bg-gradient-to-r from-amber-50 via-astro-bg to-amber-50/50 border-2 border-[#E2DED5]/80 rounded-xl p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#E2DED5]/60 pb-2.5">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-[#52635F] shrink-0" />
              <span className="font-bold text-[#263633] text-sm font-serif">
                {lang === 'hi' ? activePeriod.nameHi : activePeriod.nameEn}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-astro-secondary/20 text-astro-secondary text-[10px] font-bold uppercase tracking-wider">
                Active Transit
              </span>
            </div>
            <div className="text-xs font-mono font-bold text-astro-secondary bg-white/80 px-2.5 py-1 rounded-lg border border-[#E2DED5] w-fit">
              {activePeriod.formattedStartDate} to {activePeriod.formattedEndDate} ({activePeriod.durationFormatted})
            </div>
          </div>

          <p className="text-xs text-astro-text leading-relaxed">
            {lang === 'hi' ? activePeriod.descriptionHi : activePeriod.descriptionEn}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
            <div className="bg-white/70 p-2.5 rounded-lg border border-[#E2DED5] space-y-0.5">
              <span className="text-[10px] font-bold uppercase text-astro-secondary-text block">
                {lang === 'hi' ? 'गोचर स्थिति' : 'Transit Position'}
              </span>
              <span className="font-semibold text-astro-primary">
                Saturn in {activePeriod.saturnSign} ({activePeriod.saturnSignHi}) • House {activePeriod.houseFromMoon} from Moon
              </span>
            </div>
            <div className="bg-white/70 p-2.5 rounded-lg border border-[#E2DED5] space-y-0.5">
              <span className="text-[10px] font-bold uppercase text-astro-secondary-text block">
                {lang === 'hi' ? 'प्रभावित क्षेत्र' : 'Primary Impact Area'}
              </span>
              <span className="font-semibold text-astro-text">
                {lang === 'hi' ? activePeriod.impactAreaHi : activePeriod.impactAreaEn}
              </span>
            </div>
            <div className="bg-white/70 p-2.5 rounded-lg border border-[#E2DED5] space-y-0.5">
              <span className="text-[10px] font-bold uppercase text-astro-secondary-text block">
                {lang === 'hi' ? 'समानांतर विंशोत्तरी दशा' : 'Coinciding Vimshottari Dasha'}
              </span>
              <span className="font-semibold text-astro-primary">
                {activePeriod.coincidingDasha || 'Under Active Sequence'}
              </span>
            </div>
          </div>

          <div className="pt-2 border-t border-[#E2DED5]/60 flex items-start gap-2 text-xs text-[#263633]">
            <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <strong className="text-astro-secondary">{lang === 'hi' ? 'अनुशंसित उपाय: ' : 'Recommended Remedial Practice: '}</strong>
              <span>{lang === 'hi' ? activePeriod.remedyHi : activePeriod.remedyEn}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5 bg-astro-card p-1 rounded-xl border border-astro-border">
          <button
            onClick={() => setActiveFilter('ALL')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activeFilter === 'ALL'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-bg'
            }`}
          >
            {lang === 'hi' ? 'सभी चक्र' : 'All Saturn Transits'} ({timeline.length})
          </button>
          <button
            onClick={() => setActiveFilter('SADE_SATI')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activeFilter === 'SADE_SATI'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-bg'
            }`}
          >
            {lang === 'hi' ? 'साढ़े साती (3 चरण)' : 'Sade Sati (3 Phases)'}
          </button>
          <button
            onClick={() => setActiveFilter('DHAIYYA')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              activeFilter === 'DHAIYYA'
                ? 'bg-astro-primary text-white shadow-xs'
                : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-bg'
            }`}
          >
            {lang === 'hi' ? 'शनि ढैय्या (चतुर्थ/अष्टम)' : 'Shani Dhaiyya (4th & 8th)'}
          </button>
        </div>

        <span className="text-[11px] text-astro-secondary-text">
          {lang === 'hi' ? 'विस्तारित विवरण हेतु किसी भी काल खंड पर क्लिक करें' : 'Click any transit window for details & remedies'}
        </span>
      </div>

      {/* Chronological List of All Shani Periods with Day/Month/Year */}
      <div className="space-y-3">
        {filteredTimeline.length === 0 ? (
          <div className="p-8 text-center bg-astro-card rounded-xl border border-astro-border text-xs text-astro-secondary-text">
            No transit periods match the selected filter.
          </div>
        ) : (
          filteredTimeline.map((item, idx) => {
            const isExpanded = expandedPeriodId === item.id;
            const isCurrent = item.status === 'Active';
            const isPast = item.status === 'Past';

            return (
              <div
                key={item.id}
                className={`rounded-xl border transition-all overflow-hidden ${
                  isCurrent
                    ? 'bg-astro-secondary-surface/70 border-[#E2DED5] shadow-sm'
                    : isPast
                    ? 'bg-astro-card/70 border-astro-border opacity-85 hover:opacity-100 hover:border-astro-primary/30'
                    : 'bg-astro-bg border-astro-primary/20 hover:border-astro-primary/50'
                }`}
              >
                {/* Period Header Row */}
                <div
                  onClick={() => toggleExpand(item.id)}
                  className="p-3.5 sm:p-4 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                >
                  <div className="flex items-center gap-3">
                    {/* Status Badge / Icon */}
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 font-bold ${
                        isCurrent
                          ? 'bg-astro-secondary-surface0 text-white shadow-xs'
                          : item.category === 'Sade Sati'
                          ? 'bg-astro-primary/10 text-astro-primary border border-astro-primary/20'
                          : 'bg-astro-accent/15 text-astro-accent border border-astro-accent/30'
                      }`}
                    >
                      {item.category === 'Sade Sati' ? (
                        item.type.includes('1st') ? '1' : item.type.includes('2nd') ? '2' : '3'
                      ) : (
                        item.houseFromMoon === 4 ? '4th' : '8th'
                      )}
                    </div>

                    <div>
                      <div className="flex items-center flex-wrap gap-2">
                        <span className="font-bold text-astro-text text-sm">
                          {lang === 'hi' ? item.nameHi : item.nameEn}
                        </span>

                        {isCurrent && (
                          <span className="px-2 py-0.5 rounded-full bg-astro-secondary-surface0 text-white text-[10px] font-bold uppercase tracking-wider animate-pulse">
                            Active Now
                          </span>
                        )}

                        <span
                          className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                            item.category === 'Sade Sati'
                              ? 'bg-astro-primary/10 text-astro-primary border-astro-primary/20'
                              : 'bg-astro-accent/15 text-astro-accent border-astro-accent/30'
                          }`}
                        >
                          {item.category}
                        </span>

                        {item.cycleNumber && (
                          <span className="px-2 py-0.5 rounded-md bg-astro-border/60 text-astro-secondary-text text-[10px] font-medium">
                            Cycle {item.cycleNumber}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-astro-secondary-text mt-0.5">
                        <span>
                          Saturn in <strong className="text-astro-primary">{item.saturnSign}</strong> ({item.saturnSignHi})
                        </span>
                        <span>•</span>
                        <span>House {item.houseFromMoon} from Moon</span>
                        {item.coincidingDasha && (
                          <>
                            <span>•</span>
                            <span className="text-astro-primary font-medium">Dasha: {item.coincidingDasha}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Date & Duration Info */}
                  <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
                    <div className="text-left sm:text-right">
                      <div className="font-mono font-bold text-astro-primary text-xs">
                        {item.formattedStartDate} → {item.formattedEndDate}
                      </div>
                      <div className="text-[10px] text-astro-secondary-text font-medium">
                        Duration: {item.durationFormatted} ({item.startDate} to {item.endDate})
                      </div>
                    </div>

                    <div className="p-1 rounded-lg bg-white/60 text-astro-secondary-text border border-astro-border">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Details Pane */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 border-t border-astro-border bg-white/80 space-y-3 text-xs animate-in fade-in slide-in-from-top-1 duration-200">
                    <p className="text-astro-text leading-relaxed">
                      {lang === 'hi' ? item.descriptionHi : item.descriptionEn}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="p-3 bg-astro-card rounded-xl border border-astro-border space-y-1">
                        <span className="text-[10px] font-bold uppercase text-astro-secondary-text block">
                          {lang === 'hi' ? 'जीवन क्षेत्र एवं प्रभाव' : 'Key Manifestation & Impact Area'}
                        </span>
                        <p className="font-semibold text-astro-primary">
                          {lang === 'hi' ? item.impactAreaHi : item.impactAreaEn}
                        </p>
                      </div>

                      <div className="p-3 bg-astro-primary/5 rounded-xl border border-astro-primary/20 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-astro-primary block flex items-center gap-1">
                          <Sparkles className="w-3.5 h-3.5" />
                          {lang === 'hi' ? 'विशिष्ट वैदिक उपाय' : 'Specific Vedic Remedy'}
                        </span>
                        <p className="text-astro-text">
                          {lang === 'hi' ? item.remedyHi : item.remedyEn}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-astro-border/70 text-[10px] text-astro-secondary-text">
                      <span>
                        Calculated using exact Sidereal Lahiri Ephemeris ingress/egress boundaries for {birthDetails.name || 'Native'}.
                      </span>
                      <span className="font-mono">
                        Exact Period: {item.startDate} to {item.endDate}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
